
import Coordinates from '@app/model/hotel/Coordinates';

export type BoundingBox = {
	minLatitude: number
	maxLatitude: number
	minLongitude: number
	maxLongitude: number
}

/**
 * Utility functions for BoundingBox calculations
 */
export const BoundingBoxUtils = {
	/**
	 * Calculate the center latitude of the bounding box
	 */
	getCenterLatitude(boundingBox: BoundingBox): number {
		const minLat = typeof boundingBox.minLatitude === 'string'
			? parseFloat(boundingBox.minLatitude)
			: boundingBox.minLatitude;
		const maxLat = typeof boundingBox.maxLatitude === 'string'
			? parseFloat(boundingBox.maxLatitude)
			: boundingBox.maxLatitude;
		return (minLat + maxLat) / 2.0;
	},

	/**
	 * Calculate the center longitude of the bounding box
	 */
	getCenterLongitude(boundingBox: BoundingBox): number {
		const minLng = typeof boundingBox.minLongitude === 'string'
			? parseFloat(boundingBox.minLongitude)
			: boundingBox.minLongitude;
		const maxLng = typeof boundingBox.maxLongitude === 'string'
			? parseFloat(boundingBox.maxLongitude)
			: boundingBox.maxLongitude;
		return (minLng + maxLng) / 2.0;
	},

	/**
	 * Get the center coordinates as a Coordinates object
	 */
	getCenterCoordinates(boundingBox: BoundingBox): Coordinates {
		return {
			lat: this.getCenterLatitude(boundingBox),
			lng: this.getCenterLongitude(boundingBox)
		};
	},

	/**
	 * The box that holds the bounding box turned by a map heading (degrees), around the same centre. A map with a
	 * heading shows the box turned, so its corners can leave a fit of the box itself. The spans are turned in metres
	 * (a degree of longitude is shorter than a degree of latitude).
	 */
	rotate(boundingBox: BoundingBox, heading: number): BoundingBox {
		const { lat, lng } = this.getCenterCoordinates(boundingBox);
		const lngScale = Math.cos(lat * Math.PI / 180);
		const halfWidth = (Number(boundingBox.maxLongitude) - Number(boundingBox.minLongitude)) / 2 * lngScale;
		const halfHeight = (Number(boundingBox.maxLatitude) - Number(boundingBox.minLatitude)) / 2;
		const cos = Math.abs(Math.cos(heading * Math.PI / 180));
		const sin = Math.abs(Math.sin(heading * Math.PI / 180));
		const width = halfWidth * cos + halfHeight * sin;
		const height = halfWidth * sin + halfHeight * cos;
		return {
			minLatitude: lat - height,
			maxLatitude: lat + height,
			minLongitude: lng - width / lngScale,
			maxLongitude: lng + width / lngScale,
		};
	},

	/**
	 * Calculate optimal Google Maps zoom level based on bounding box dimensions with 200m padding
	 * Uses precise mathematical calculation to fit the bounding box exactly to the viewport
	 */
	calculateOptimalZoom(boundingBox: BoundingBox, mapWidth: number = 800, mapHeight: number = 600): number {
		const minLat = typeof boundingBox.minLatitude === 'string'
			? parseFloat(boundingBox.minLatitude)
			: boundingBox.minLatitude;
		const maxLat = typeof boundingBox.maxLatitude === 'string'
			? parseFloat(boundingBox.maxLatitude)
			: boundingBox.maxLatitude;
		const minLng = typeof boundingBox.minLongitude === 'string'
			? parseFloat(boundingBox.minLongitude)
			: boundingBox.minLongitude;
		const maxLng = typeof boundingBox.maxLongitude === 'string'
			? parseFloat(boundingBox.maxLongitude)
			: boundingBox.maxLongitude;

		// Add 200 meter padding to the bounding box
		// Convert 200 meters to degrees
		// 1 degree latitude ≈ 111,320 meters
		const latPaddingInDegrees = 200 / 111320;

		// For longitude, account for latitude compression
		const centerLat = (minLat + maxLat) / 2;
		const metersPerDegreeLongitude = 111320 * Math.cos(centerLat * Math.PI / 180);
		const lngPaddingInDegrees = 200 / metersPerDegreeLongitude;

		// Apply padding to bounding box
		const paddedMinLat = minLat - latPaddingInDegrees;
		const paddedMaxLat = maxLat + latPaddingInDegrees;
		const paddedMinLng = minLng - lngPaddingInDegrees;
		const paddedMaxLng = maxLng + lngPaddingInDegrees;

		// Calculate the span of the padded bounding box
		const latSpan = paddedMaxLat - paddedMinLat;
		const lngSpan = paddedMaxLng - paddedMinLng;

		// Calculate zoom level for longitude
		// Google Maps uses 256 pixel tiles, and the world is 256 pixels wide at zoom 0
		// At zoom level z, the world is 256 * 2^z pixels wide
		// The formula: zoom = log2(360 * mapWidth / (lngSpan * 256))
		const lngZoom = Math.log2(360 * mapWidth / (lngSpan * 256));

		// Calculate zoom level for latitude using Mercator projection
		// Convert latitude to radians
		const latRad1 = paddedMinLat * Math.PI / 180;
		const latRad2 = paddedMaxLat * Math.PI / 180;
		
		// Calculate the Mercator Y coordinates
		const mercatorY1 = Math.log(Math.tan(Math.PI / 4 + latRad1 / 2));
		const mercatorY2 = Math.log(Math.tan(Math.PI / 4 + latRad2 / 2));
		
		// Calculate the Mercator span
		const mercatorSpan = Math.abs(mercatorY2 - mercatorY1);
		
		// Calculate zoom for latitude
		// The world height in Mercator projection is 2π
		const latZoom = Math.log2(2 * Math.PI * mapHeight / (mercatorSpan * 256));

		// Use the minimum zoom to ensure both dimensions fit
		const calculatedZoom = Math.min(lngZoom, latZoom);

		// Apply a smaller adjustment factor to ensure complete visibility (0.02 zoom levels)
		// This provides a small buffer without being too aggressive
		const adjustedZoom = calculatedZoom - 0.02;

		// Clamp to valid Google Maps zoom range (0-21)
		const finalZoom = Math.max(0, Math.min(21, adjustedZoom));

		console.log('BoundingBox calculateOptimalZoom:', {
			originalBounds: { minLat, maxLat, minLng, maxLng },
			paddedBounds: { paddedMinLat, paddedMaxLat, paddedMinLng, paddedMaxLng },
			spans: { latSpan, lngSpan },
			zoomCalculations: { lngZoom, latZoom, calculatedZoom, adjustedZoom },
			finalZoom,
			mapDimensions: { width: mapWidth, height: mapHeight }
		});

		return finalZoom;
	}
};
